import { z } from 'zod';
import { MAIL_ATTACHMENT_COUNT_LIMIT } from './mail.js';

/**
 * Every schema in this file describes a WORKSPACE SNAPSHOT — the
 * sessionStorage blob that lets a tab switch return an agent to their
 * filters, scroll position, and half-typed draft. That makes the bounds
 * here fundamentally different from the bounds on a request schema:
 *
 *   - A request schema REJECTS oversize input. The server must refuse
 *     work it can't safely do, and the caller gets a real error.
 *   - A snapshot schema CLAMPS oversize input. There is no caller to
 *     report to — the write happens in a React effect, so a thrown
 *     ZodError unwinds into the route's error boundary and takes the
 *     whole module down with it. That is not a hypothetical: attaching
 *     an 11th file to an email used to blank the entire Mail page,
 *     because `uploadedAttachments` was `.max(10)` and the composer
 *     enforced nothing.
 *
 * So the bounds below are expressed with `boundedArray` / `boundedText`,
 * which slice and truncate instead of failing. They are the last line of
 * defence, not the first — the composer caps attachments at
 * `MAIL_ATTACHMENT_COUNT_LIMIT` itself so a clamp here is never what the
 * agent actually experiences. Truncating a body silently would be its own
 * kind of lie, which is precisely why the limits are enforced upstream
 * where they can be explained.
 */

/** Bounded snapshot array: slices past `max` rather than rejecting. */
function boundedArray<T extends z.ZodTypeAny>(item: T, max: number) {
  return z
    .array(item)
    .transform((items) => (items.length > max ? items.slice(0, max) : items));
}

/**
 * Bounded snapshot text: truncates past `max` rather than rejecting.
 *
 * Reserved for FREE TEXT — what the agent typed or pasted, plus
 * display names. Identifiers and URLs (folder names, Graph message /
 * chat ids, download URLs) deliberately keep their rejecting `.max()`:
 * a truncated id resolves to nothing, so it is better for the read path
 * to discard that field and fall back than to restore a pointer that
 * silently aims at the wrong thing.
 */
function boundedText(max: number, opts: { trim?: boolean } = {}) {
  const base = opts.trim === true ? z.string().trim() : z.string();
  return base.transform((s) => (s.length > max ? s.slice(0, max) : s));
}

export const MailWorkspaceDateRange = z.enum(['all', 'today', 'last7', 'last30']);
export type MailWorkspaceDateRange = z.infer<typeof MailWorkspaceDateRange>;

export const MailWorkspaceAttachment = z.object({
  id: z.number().int().positive(),
  fileName: z.string().trim().min(1).max(255),
  sizeBytes: z.number().int().nonnegative().nullable(),
  mimeType: z.string().trim().max(255).nullable(),
});
export type MailWorkspaceAttachment = z.infer<typeof MailWorkspaceAttachment>;

export const MailWorkspaceAttachmentDownload = z.object({
  fileName: z.string().trim().min(1).max(255),
  downloadUrl: z.string().trim().min(1).max(2_000),
  mimeType: z.string().trim().max(255).nullable(),
});
export type MailWorkspaceAttachmentDownload = z.infer<typeof MailWorkspaceAttachmentDownload>;

/**
 * An attachment that lives on the Outlook draft rather than in our media
 * table — anything `mail_attach_drive_item` / `mail_attach_file` pushed on
 * server-side. Metadata only: the bytes stay on the message, and the send
 * carries them because it fires the draft in place.
 */
export const MailWorkspaceGraphAttachment = z.object({
  attachmentId: z.string().trim().min(1).max(1_024),
  fileName: z.string().trim().min(1).max(512),
  sizeBytes: z.number().int().nonnegative(),
  mimeType: z.string().trim().max(255).nullable(),
});
export type MailWorkspaceGraphAttachment = z.infer<typeof MailWorkspaceGraphAttachment>;

export const MailWorkspaceComposeSnapshot = z.object({
  open: z.literal(true),
  to: boundedText(4_000).default(''),
  cc: boundedText(4_000).default(''),
  bcc: boundedText(4_000).default(''),
  // Outlook accepts subjects longer than this, and a forwarded MLS or
  // offer subject really does run past 255 characters — clamped, not
  // rejected, so a long subject costs the tail of a header rather than
  // the whole draft.
  subject: boundedText(255).default(''),
  bodyHtml: boundedText(1_000_000).default(''),
  activeSignatureId: z.number().int().positive().nullable().optional(),
  signatureOptionsAvailable: z.boolean().optional(),
  // A reply quoting a message with inline base64 images clears 1MB
  // easily; the quote is reconstructible from the source message, so
  // losing its tail is survivable in a way losing the draft is not.
  quotedHtml: boundedText(1_000_000).optional(),
  editingDraftMessageId: z.number().int().positive().optional(),
  editingDraftHadAttachments: z.boolean().optional(),
  /** Attachments already on the resumed Outlook draft. Kept in the
   *  snapshot so a tab switch doesn't make them look like they vanished. */
  graphAttachments: boundedArray(MailWorkspaceGraphAttachment, 25).default([]),
  uploadedAttachments: boundedArray(
    MailWorkspaceAttachment,
    MAIL_ATTACHMENT_COUNT_LIMIT,
  ).default([]),
  attachmentDownloads: boundedArray(
    MailWorkspaceAttachmentDownload,
    MAIL_ATTACHMENT_COUNT_LIMIT,
  ).default([]),
  updatedAt: z.number().int().nonnegative(),
});
export type MailWorkspaceComposeSnapshot = z.infer<typeof MailWorkspaceComposeSnapshot>;

export const MailWorkspaceState = z.object({
  /** Selected mailbox (mail_accounts id). Null = primary. Persisted so
   *  tab switches don't bounce the agent back to their primary inbox. */
  accountId: z.number().int().positive().nullable().default(null),
  folder: z.string().trim().min(1).max(120).default('inbox'),
  selectedId: z.number().int().positive().nullable().default(null),
  search: boundedText(500).default(''),
  matchedOnly: z.boolean().default(false),
  attachmentsOnly: z.boolean().default(false),
  attachmentsAndContactsOnly: z.boolean().default(false),
  unreadOnly: z.boolean().default(false),
  readOnly: z.boolean().default(false),
  flaggedOnly: z.boolean().default(false),
  /** Outlook category ("label") names the inbox is filtered to. Empty
   *  = no label filter. Match is AND — a message must carry every
   *  selected label to show. Holds raw Graph names (e.g. "Blue
   *  category") plus any custom user-defined category names. Capped so a
   *  malformed snapshot can't bloat sessionStorage. Older clients
   *  default to []. */
  labels: boundedArray(z.string().trim().min(1).max(255), 50).default([]),
  dateRange: MailWorkspaceDateRange.default('all'),
  listScrollTop: z.number().int().nonnegative().default(0),
  compose: MailWorkspaceComposeSnapshot.nullable().default(null),
  updatedAt: z.number().int().nonnegative(),
});
export type MailWorkspaceState = z.infer<typeof MailWorkspaceState>;

export const TeamsWorkspaceView = z.enum(['channels', 'chats', 'members', 'drafts']);
export type TeamsWorkspaceView = z.infer<typeof TeamsWorkspaceView>;

export const TeamsWorkspaceState = z.object({
  view: TeamsWorkspaceView.default('chats'),
  selectedTeamId: z.string().trim().min(1).max(500).nullable().default(null),
  selectedTeamName: boundedText(500, { trim: true }).nullable().default(null),
  selectedChannelId: z.string().trim().min(1).max(500).nullable().default(null),
  selectedChannelName: boundedText(500, { trim: true }).nullable().default(null),
  selectedChannelWebUrl: z.string().trim().max(2_000).nullable().default(null),
  selectedChatId: z.string().trim().min(1).max(500).nullable().default(null),
  selectedChatName: boundedText(500, { trim: true }).nullable().default(null),
  selectedChatWebUrl: z.string().trim().max(2_000).nullable().default(null),
  updatedAt: z.number().int().nonnegative(),
});
export type TeamsWorkspaceState = z.infer<typeof TeamsWorkspaceState>;

/**
 * Calendar module workspace state. The CalendarPage lives behind a
 * lazy-loaded route, so every tab switch unmounts the component and
 * destroys local `useState` form drafts — including the "New
 * appointment" modal an agent may be halfway through typing. This
 * snapshot is the durable backing store: it captures the current view
 * (week/month + anchor date + selected event/day) and any in-progress
 * compose-or-edit form so a tab switch + return restores the user to
 * exactly where they left off. Mirrors the `MailWorkspaceState` shape.
 *
 * Appointment-type is held as a plain string (not the strict zod enum)
 * so a snapshot survives schema migrations — if a new type is added
 * and the snapshot still carries an old value, the form falls back to
 * the default rather than throwing on parse.
 */
export const CalendarWorkspaceView = z.enum(['day', 'week', 'month']);
export type CalendarWorkspaceView = z.infer<typeof CalendarWorkspaceView>;

export const CalendarWorkspaceComposeKind = z.enum(['create', 'edit']);
export type CalendarWorkspaceComposeKind = z.infer<
  typeof CalendarWorkspaceComposeKind
>;

export const CalendarWorkspaceComposeSnapshot = z.object({
  kind: CalendarWorkspaceComposeKind,
  /** For `edit`, the MS Graph event id the form was opened against. */
  eventId: z.string().trim().min(1).max(500).nullable().default(null),
  /** For `create`, the day the agent picked on the grid before opening compose. */
  defaultDateIso: z.string().trim().min(1).max(64).nullable().default(null),
  subject: boundedText(500).default(''),
  appointmentType: z.string().trim().max(64).default('showing'),
  location: boundedText(500).default(''),
  isAllDay: z.boolean().default(false),
  /** Local-time input values, exactly as the form holds them. */
  startLocal: z.string().max(64).default(''),
  endLocal: z.string().max(64).default(''),
  // Agents paste whole emails into an appointment body; 20k is generous
  // but reachable, and losing the tail beats losing the appointment.
  /** Client-facing. Becomes the invite body the attendee reads. */
  inviteMessage: boundedText(20_000).default(''),
  /** Agent-only draft fields — see the audience split in calendar.ts. */
  internalTitle: boundedText(500).default(''),
  privateNotes: boundedText(20_000).default(''),
  /** Attached MLS listings, enough to re-render the chips on restore. */
  listings: z
    .array(z.object({
      market: z.string().trim().max(16),
      listingKey: z.string().trim().max(128),
      label: boundedText(300).default(''),
      subLabel: boundedText(300).nullable().default(null),
    }))
    .max(25)
    .default([]),
  updatedAt: z.number().int().nonnegative(),
});
export type CalendarWorkspaceComposeSnapshot = z.infer<
  typeof CalendarWorkspaceComposeSnapshot
>;

export const CalendarWorkspaceState = z.object({
  view: CalendarWorkspaceView.default('month'),
  /** ISO instant of the anchor date (which day/week/month the grid is showing). */
  anchorIso: z.string().trim().max(64).nullable().default(null),
  /** Currently inspected event id (right-panel detail view). */
  selectedEventId: z.string().trim().min(1).max(500).nullable().default(null),
  /** Currently inspected day (right-panel "what's on this day" view). */
  selectedDateIso: z.string().trim().max(64).nullable().default(null),
  /** In-progress create-or-edit form draft. Null when the form is closed. */
  compose: CalendarWorkspaceComposeSnapshot.nullable().default(null),
  updatedAt: z.number().int().nonnegative(),
});
export type CalendarWorkspaceState = z.infer<typeof CalendarWorkspaceState>;

/**
 * Good Faith Estimates module workspace state. The GFE workbench lives
 * behind a lazy-loaded route, so every tab switch unmounts the page and
 * destroys the editor's local `useState` draft — the half-filled buyer
 * estimate or seller net sheet an agent is in the middle of typing.
 * This snapshot is the durable backing store: it remembers which
 * estimate (or the new-estimate builder) was open plus the in-progress
 * edits, so a tab switch + return drops the agent back exactly where
 * they left off. Mirrors `CalendarWorkspaceState`.
 *
 * The draft + line buffers are stored as loose records (not a strict
 * mirror of the client's `EstimateDraft`) so the snapshot survives
 * field additions: rehydration merges the buffer over a fresh
 * server/empty draft, so any field the snapshot is missing falls back
 * rather than throwing on parse. Caps keep a malformed snapshot from
 * bloating sessionStorage.
 */
export const GfeWorkspaceSide = z.enum(['buyer', 'seller']);
export type GfeWorkspaceSide = z.infer<typeof GfeWorkspaceSide>;

/** In-progress NEW estimate (not yet persisted server-side). */
export const GfeDraftBuffer = z.object({
  draft: z.record(z.unknown()),
  lines: boundedArray(z.record(z.unknown()), 500).default([]),
  updatedAt: z.number().int().nonnegative(),
});
export type GfeDraftBuffer = z.infer<typeof GfeDraftBuffer>;

/** Unsaved edits to an existing estimate, held in the `editors` map
 *  keyed by estimate id. */
export const GfeWorkspaceEditorBuffer = z.object({
  /** Server `updatedAt` the edits branched from. If the live estimate
   *  has advanced past this (saved elsewhere), the buffer is treated as
   *  stale and the server copy wins. */
  serverUpdatedAt: z.string().max(64).nullable().default(null),
  draft: z.record(z.unknown()),
  lines: boundedArray(z.record(z.unknown()), 500).default([]),
  updatedAt: z.number().int().nonnegative(),
});
export type GfeWorkspaceEditorBuffer = z.infer<typeof GfeWorkspaceEditorBuffer>;

export const GfeWorkspaceState = z.object({
  /** Which workbench panel was open: an existing estimate or the
   *  new-estimate builder. */
  mode: z.enum(['existing', 'creating']).default('existing'),
  /** Selected existing estimate id (null = fall back to most recent). */
  selectedId: z.string().trim().max(100).nullable().default(null),
  /** Side of the new-estimate builder when mode === 'creating'. */
  draftSide: GfeWorkspaceSide.default('buyer'),
  /** Unsaved edits to existing estimates, keyed by estimate id. An entry
   *  is dropped when its estimate is saved (server then matches) or the
   *  edits are reverted. */
  editors: z.record(GfeWorkspaceEditorBuffer).default({}),
  /** Unsaved in-progress NEW estimate. Null when the builder is closed
   *  or untouched. */
  creating: GfeDraftBuffer.nullable().default(null),
  updatedAt: z.number().int().nonnegative(),
});
export type GfeWorkspaceState = z.infer<typeof GfeWorkspaceState>;

/**
 * Unsaved typing on one Lone Wolf form, keyed per user, file and form
 * version. Both form editors (the on-the-form overlay and the field list)
 * share one draft, so switching between them, to the Filled PDF, to another
 * form, or reloading the tab brings the same pending values back.
 *
 * It exists because a failed save used to be the only copy of the broker's
 * work: on 2026-10-21 every save was being refused upstream, and one click
 * on "Filled PDF" to check what printed discarded the whole contract's
 * typing with no prompt. The draft is a convenience and never the source of
 * truth — a save that confirms a key drops it from here.
 */
export const TransactFormDraft = z.object({
  /** Datum key -> typed value. Keys are datum ids, so they keep a rejecting cap. */
  edits: z
    .record(z.string().max(128), boundedText(20_000))
    .transform((edits) => {
      const entries = Object.entries(edits);
      return entries.length > 400 ? Object.fromEntries(entries.slice(0, 400)) : edits;
    }),
  updatedAt: z.number().int().nonnegative(),
});
export type TransactFormDraft = z.infer<typeof TransactFormDraft>;

/**
 * CRM contact-detail workspace state — the Activity timeline's search,
 * sort, and filter selection.
 *
 * `ContactDetailPage` sits behind a lazy route (CLAUDE.md rule 5), so a
 * tab switch unmounts it and destroys every `useState` in the subtree.
 * Before this snapshot existed, an agent who filtered the timeline to
 * Calls, typed a search, then glanced at another tab came back to an
 * unfiltered, unsearched feed — while the SMS composer a few inches up
 * the same page restored its half-typed draft, because that one already
 * persisted (`ContactCommComposer`).
 *
 * Keyed per user AND per contact: a filter remembered from one contact
 * and silently applied to the next is worse than resetting, because the
 * agent reads the shortened feed as "this person has no calls".
 *
 * Session-scoped (sessionStorage), matching Calendar and GFE: a view
 * filter should survive a tab switch, not greet the agent tomorrow.
 */
export const CrmActivityWorkspaceSort = z.enum(['newest', 'oldest']);
export type CrmActivityWorkspaceSort = z.infer<typeof CrmActivityWorkspaceSort>;

export const CrmContactWorkspaceState = z.object({
  /**
   * Selected kind filters. Empty array = no kind restriction ("All").
   * Held as plain strings rather than the `ActivityKind` enum so a
   * snapshot written by a newer build — one that knows a kind this
   * build does not — degrades to ignoring the unknown entry instead of
   * failing the parse and blanking the agent's whole selection.
   */
  activityKinds: boundedArray(z.string().trim().min(1).max(40), 16).default([]),
  activitySearch: boundedText(200).default(''),
  activitySort: CrmActivityWorkspaceSort.default('newest'),
  /** ISO date (yyyy-mm-dd) bounds from the date-range filter. */
  activityFrom: z.string().trim().max(32).nullable().default(null),
  activityTo: z.string().trim().max(32).nullable().default(null),
  /** Whether the filter panel is expanded. */
  activityFiltersOpen: z.boolean().default(false),
  /**
   * Refinements applied to the rows already on screen: direction, and
   * the unread / missed / has-attachment flags. Unlike the kind, date,
   * and search filters these are NOT pushed to the server — every value
   * they read is already on the row — so they narrow the loaded page
   * rather than the contact's whole history.
   */
  activityDirection: z.enum(['any', 'inbound', 'outbound']).default('any'),
  activityUnreadOnly: z.boolean().default(false),
  activityMissedOnly: z.boolean().default(false),
  activityWithFilesOnly: z.boolean().default(false),
  updatedAt: z.number().int().nonnegative(),
});
export type CrmContactWorkspaceState = z.infer<typeof CrmContactWorkspaceState>;
